import { useState, useEffect } from "react";
import { collection, query, where, orderBy, getDocs, limit } from "firebase/firestore";
import { db } from "../firebase";
import type { DailyBillingSnapshot } from "@winche/shared";
import {
  Block,
  EmptyState,
  ErrorMessage,
  LoadingBlock,
  Metric,
  MetricGrid,
  Panel,
  SectionHeader,
  Segmented,
  SourceNote,
} from "../components/ui";
import { describeFailure, type AdminFailure } from "../lib/callable-error";
import { Chart, type ChartDatum } from "../components/chart";
import { shortDay, usd, usdCompact, usdUnit } from "../lib/format";

type Window = "7d" | "30d" | "90d" | "12m";

const WINDOWS: { id: Window; days: number }[] = [
  { id: "7d", days: 7 },
  { id: "30d", days: 30 },
  { id: "90d", days: 90 },
  { id: "12m", days: 365 },
];

function toDateString(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Billing trend (admin-04/05): list MRR is a stock and AI cost a daily flow,
 * so they get separate charts with the same X instead of one axis where the
 * AI line hugs the floor. Missing contribution is a gap, never $0.
 */
export function NetBillingSection() {
  const [win, setWin] = useState<Window>("30d");
  const [snapshots, setSnapshots] = useState<DailyBillingSnapshot[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AdminFailure | null>(null);

  useEffect(() => {
    const days = WINDOWS.find((w) => w.id === win)!.days;
    const startKey = toDateString(new Date(Date.now() - days * 86_400_000));
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const snap = await getDocs(
          query(collection(db, "_admin", "billingHistory", "snapshots"), where("date", ">=", startKey), orderBy("date"), limit(400)),
        );
        if (!cancelled) setSnapshots(snap.docs.map((d) => d.data() as DailyBillingSnapshot));
      } catch (err) {
        if (cancelled) return;
        const failure = describeFailure("Reading _admin/billingHistory", err);
        setError(
          failure.code === "permission-denied"
            ? {
                ...failure,
                detail: "Your admin claim may not be in the current session — sign out and back in to refresh it.",
              }
            : failure,
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [win]);

  const last = snapshots && snapshots.length > 0 ? snapshots[snapshots.length - 1] : null;
  const first = snapshots && snapshots.length > 1 ? snapshots[0] : null;
  const lastContribution = last?.contributionUsd ?? null;
  const firstContribution = first?.contributionUsd ?? null;
  const netTrend = lastContribution != null && firstContribution != null ? lastContribution - firstContribution : null;

  const points: ChartDatum[] = (snapshots ?? []).map((s) => ({
    x: s.date,
    xLabel: shortDay(s.date),
    tooltipLabel: `${shortDay(s.date)}${s.completeness && s.completeness !== "complete" ? ` · ${s.completeness}` : ""}`,
    values: {
      mrr: s.listMrrUsd ?? s.mrrUsd,
      ai: s.aiCostCompleteness === "missing" ? null : s.aiCostUsd,
      contribution: s.contributionUsd ?? null,
    },
  }));
  const hasContribution = points.some((p) => p.values.contribution != null);

  return (
    <div>
      <SectionHeader
        title="Billing Trend"
        subtitle="Daily list MRR and UTC AI cost. Contribution appears only on days with collections."
        action={
          <Segmented
            label="Window"
            value={win}
            onChange={setWin}
            options={WINDOWS.map((w) => ({ value: w.id, label: w.id }))}
          />
        }
      />

      {error && <ErrorMessage failure={error} />}

      {loading && snapshots === null && <LoadingBlock metrics={4} rows={0} label="Loading billing snapshots" />}

      {last && (
        <MetricGrid cols={4} className="mb-6">
          <Metric label="List MRR" value={usd(last.listMrrUsd ?? last.mrrUsd)} sub={`On ${shortDay(last.date)}`} />
          <Metric label="AI cost (last day)" value={last.aiCostCompleteness === "missing" ? "—" : usdUnit(last.aiCostUsd)} />
          <Metric
            label="Contribution (last day)"
            value={usd(lastContribution)}
            tone={lastContribution == null ? "default" : lastContribution < 0 ? "bad" : "default"}
            hint="Collections minus same-day AI cost. — when there were no collections."
          />
          <Metric
            label={`Contribution change · ${win}`}
            value={netTrend == null ? "—" : `${netTrend >= 0 ? "+" : "−"}${usd(Math.abs(netTrend))}`}
            tone={netTrend == null ? "default" : netTrend < 0 ? "bad" : "good"}
          />
        </MetricGrid>
      )}

      {!loading && snapshots !== null && snapshots.length === 0 && (
        <EmptyState message="No billing snapshots yet. They accumulate nightly at 23:50 UTC." />
      )}

      {snapshots && snapshots.length > 0 && (
        <div className={loading ? "opacity-60 transition-opacity" : undefined} aria-busy={loading || undefined}>
          <Block title="List MRR" hint="Catalog price × active PRO headcount (a stock).">
            <Panel>
              <Chart
                title="List MRR per day"
                data={points}
                series={[{ key: "mrr", label: "List MRR", color: "var(--chart-1)", kind: "line" }]}
                format={usd}
                formatAxis={usdCompact}
                height={160}
              />
            </Panel>
          </Block>
          <Block title={hasContribution ? "AI cost and contribution per day" : "AI cost per day"} hint="Daily flows, same X as the chart above.">
            <Panel>
              <Chart
                title="AI cost and contribution per day"
                data={points}
                series={[
                  { key: "ai", label: "AI cost", color: "var(--chart-2)", kind: "bar" },
                  ...(hasContribution ? [{ key: "contribution", label: "Contribution", color: "var(--chart-5)", kind: "line" as const }] : []),
                ]}
                format={usdUnit}
                formatAxis={usdCompact}
                height={160}
              />
              <SourceNote>Source: _admin/billingHistory/snapshots, nightly. In-progress months stay incomplete.</SourceNote>
            </Panel>
          </Block>
        </div>
      )}
    </div>
  );
}
